import { countryCodeDict } from 'countries'
import { logoUrls } from 'urls'
import _ from 'lodash'
import store from '../../app/store'

const getLinkedAccountDisplayName = (merchantId) => {
  const accounts = store.getState().root.accounts || []
  const account = accounts.find((entry) => entry.merchant_id === merchantId)
  if (!account) return null
  return account.store_name || account.account_name || account.store_url || account.merchant_id
}

const getMerchantFilterLogo = (channel) => {
  if (!channel) return null
  return logoUrls[channel] || logoUrls[channel.toLowerCase()] || logoUrls[channel.replace(/\s+/g, '').toLowerCase()]
}

export const reportFilterConfigs = {
  shipping_zip: {
    title: 'Zip Code (Within Range)',
  }
}

export const filterConfigs = {
  merchant_id: {
    title: 'Account',
    decodeFunc: (bucket) => {
      const title = bucket.email || getLinkedAccountDisplayName(bucket.val) || bucket.val
      return {
        ...bucket,
        title,
        logo: getMerchantFilterLogo(bucket.channel),
      }
    },
    notEmpty: true,
  },
  channel: {
    title: 'Channel',
    decodeFunc: (bucket) => {
      return {
        ...bucket,
        logo: logoUrls[bucket.val],
      }
    },
  },
  financial_status: {
    title: 'Payment Status',
  },
  currency: {
    title: 'Currency',
  },
  billing_country_code: {
    title: 'Country/Region Bought In',
    decodeFunc: (bucket) => {
      return { ...bucket, title: bucket.title || countryCodeDict[bucket.val] || bucket.val }
    }
  },
  billing_province: {
    title: 'State/Province Bought In',
    decodeFunc: (bucket) => {
      return { ...bucket, title: bucket.val }
    }
  },
  billing_county_name: {
    title: 'County Bought In'
  },
  billing_city: {
    title: 'City Bought In'
  },
  shipping_country_code: {
    title: 'Country/Region Shipped To',
    decodeFunc: (bucket) => {
      return { ...bucket, title: bucket.title || countryCodeDict[bucket.val] || bucket.val }
    }
  },
  shipping_province: {
    title: 'State/Province Shipped To'
  },
  shipping_county_name: {
    title: 'County Shipped To'
  },
  shipping_city: {
    title: 'City Shipped To'
  },
  customer_id: {
    title: 'Customer',
  },
  customer_group_name: {
    title: 'Customer Group',
    defaultHidden: true,
  },
  processing_method: {
    title: 'Processing Method',
  },
  gateway: {
    title: 'Payment Gateway',
  },
  payment_method: {
    title: 'Payment Method',
  },
  credit_card_type: {
    title: 'Credit Card Type',
  },
  source_name: {
    title: 'Source',
  },
  app_id: {
    title: 'Sales Channel ID',
  },
  fulfillment_status: {
    title: 'Fulfillment Status',
  },
  fulfillment_full_address: {
    title: 'Fulfillment Address',
  },
  shipping_providers: {
    title: 'Shipping Provider',
  },
  shipping_methods: {
    title: 'Shipping Method',
  },
  coupon_codes: {
    title: 'Coupon Code',
  },
  product_ids: {
    title: 'Product',
    defaultHidden: true,
  },
  variant_ids: {
    title: 'Variant',
    defaultHidden: true,
  },
  brand_ids: {
    title: 'Brand',
  },
  brand_names: {
    title: 'Brand name',
    defaultHidden: true,
  },
  marketplace_product_ids: {
    title: 'Marketplace Product ID',
  },
  skus: {
    title: 'SKU',
  },
  product_skus: {
    title: 'Product SKU'
  },
  bin_picking_numbers: {
    title: 'Bin Picking Number'
  },
  vendors: {
    title: 'Vendor',
  },
  product_types: {
    title: 'Product Tags',
  },
  category_titles: {
    title: 'Category',
  },
  catalog_titles_v1: {
    title: 'Catalog',
  },
  order_tags: {
    title: 'Tags',
  },
  tax_exempt_category: {
    title: 'Tax Exempt Category',
    defaultHidden: true,
  },
  utm_source: {
    title: 'UTM Source',
  },
  utm_medium: {
    title: 'UTM Medium',
  },
  utm_campaign: {
    title: 'UTM Campaign',
  },
  utm_content: {
    title: 'UTM Content',
  },
  utm_term: {
    title: 'UTM Term',
  },
  utm_id: {
    title: 'UTM ID',
  },
  sales_channel_name: {
    title: 'Sales Channel Name',
  },
  sales_channel_type: {
    title: 'Sales Channel Type',
  },
  fulfillment_channel_type: {
    title: 'Fulfillment Channel (FBA/FBM)',
    defaultHidden: true,
  },
  sales_channel_platform: {
    title: 'Sales Channel Platform',
  },
  taxed: {
    title: 'Taxed',
    defaultHidden: true,
  },
  discounted: {
    title: 'Discounted',
    defaultHidden: true,
  },
  noted: {
    title: 'Staff Note',
    defaultHidden: true,
  },
  guest: {
    title: 'Guest',
    defaultHidden: true,
  },
  gateway_fee: {
    title: 'Gateway Fee',
    defaultHidden: true,
  },
  backcharges_cost: {
    title: 'Shipping Backcharges',
    defaultHidden: true,
  },
  points_status: {
    title: 'Loyalty Points',
  },
  gift_cards: {
    title: 'Gift Card',
  },
  is_first_order: {
    title: 'New vs Returning Customer',
    defaultHidden: true,
  },
  bank_ref_id: {
    title: 'Payout Bank Reference ID',
    defaultHidden: true,
  },
}

// Static bucket labels for vs/opposite facets when /filters returns no Solr counts yet.
export const oppositeFacetFilterValues: Record<string, { val: string }[]> = {
  is_first_order: [{ val: 'New' }, { val: 'Returning' }],
}

export const mergeReportFilterPropertyOption = (key: string, apiValue?: { val: string }[], label?: string) => {
  const value = apiValue?.length ? apiValue : (oppositeFacetFilterValues[key] || [])
  return {
    key,
    value,
    label: (filterConfigs as Record<string, { title?: string }>)[key]?.title || label || key,
  }
}

const filterTypeTitles = {
  option: { title: 'Product Options' },
  custom_field: { title: 'Custom Product Fields' },
  customer_form_field: { title: 'Customer Form Fields' },
  shipping_form_field: { title: 'Shipping Form Fields' },
  customer_custom_attribute: { title: 'Customer Attributes' },
  order_metafield: { title: 'Order Metafields' },
  product_metafield: { title: 'Product Metafields' },
}

export const allFilterFormatters = {
  keywords: { title: 'Keyword(s)' },
  ...filterConfigs,
  ...filterTypeTitles,
}

export const filterArrangement = (facets, filterType = '', userConfigs = {}, forFilterEditor = false) => {
  // Default secondary filter types to visible when absent from saved configs (e.g. newly added types)
  if (filterType && !forFilterEditor && !_.get(userConfigs, [filterType, 'visible'], true)) {
    return []
  }
  const results = [], iterable = filterType ? facets : filterConfigs
  let index = 0
  for (var key in iterable) {
    // For secondary filters (options, custom_fields, etc.), skip if no data
    // For general filters, always show configured filters to prevent layout shift
    if (filterType && facets && (!facets[key] || !facets[key].length)) {
      continue
    }
    if (!filterType && !facets && filterConfigs[key].defaultHidden) {
      continue
    }
    if (!filterType && !_.get(userConfigs, [key, 'visible'], filterConfigs[key]?.defaultHidden ? false : true)) {
      continue
    }
    if (index && index % 3 == 2) {
      results.push({ clearFix: true })
    }
    results.push({
      key,
      title: (filterConfigs[key] || {}).title || key,
      decodeFunc: (filterConfigs[key] || {}).decodeFunc || null,
      filterType,
    })
    index ++
  }
  if (results.length && filterType) {
    results.unshift({ header: true, title: filterTypeTitles[filterType].title })
  }
  return results
}

export const initialFilterConfigs = Object.assign({}, Object.keys(filterConfigs).filter(
  k => !['product_ids', 'variant_ids'].includes(k)
).reduce((acc, cur, index) => ({
  ...acc,
  [cur]: { visible: true, order: index }
}), {}), {
  option: { visible: true },
  custom_field: { visible: true },
  customer_form_field: { visible: true },
  shipping_form_field: { visible: true },
  customer_custom_attribute: { visible: true },
  order_metafield: { visible: true },
  product_metafield: { visible: true },
})